/**
 * Shared auth bootstrap for the unified web PWA (capture + execute).
 *
 * Phone-verified sessions must survive opening a new /t/… or /q/… link
 * in the same browser. We wait for IndexedDB restore before creating a
 * new anonymous user (which would overwrite the saved phone session),
 * and keep a local marker so slow restores on mobile still get a retry
 * window.
 */
import {
  onAuthStateChanged,
  signInAnonymously,
  signOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { getAuthInitAt, getFirebaseAuth } from '../config/firebase';

const PHONE_SESSION_KEY = 'x9_web_phone_verified';

/**
 * Nothing in this bootstrap may block the first paint forever. Callers render
 * a spinner while it runs, so an unbounded await here is indistinguishable
 * from a hung app (2026-08-30: a staff member on a weak mobile connection
 * watched "Opening run…" indefinitely and the callable was never issued).
 */
const SESSION_TIMEOUT_MS = 15000;
const HYDRATE_TIMEOUT_MS = 6000;

export const SESSION_TIMEOUT_CODE = 'auth/session-timeout';

class SessionTimeoutError extends Error {
  readonly code = SESSION_TIMEOUT_CODE;

  constructor(label: string) {
    super(`Timed out waiting for ${label}`);
    this.name = 'SessionTimeoutError';
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new SessionTimeoutError(label)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

let sessionPromise: Promise<User> | null = null;
/** Bumped on sign-out so an in-flight bootstrap cannot overwrite a newer session. */
let sessionGeneration = 0;

export function markPhoneSessionVerified(): void {
  try {
    localStorage.setItem(PHONE_SESSION_KEY, String(Date.now()));
  } catch {
    // localStorage may be unavailable in some embedded browsers.
  }
}

/**
 * End the phone-verified session (D21).
 *
 * The marker has to go first and unconditionally: it is what makes the
 * bootstrap wait for a persisted phone user instead of creating an anonymous
 * one, so leaving it behind would strand the next visit waiting for a session
 * that is never coming back. The cached bootstrap promise is dropped for the
 * same reason — it resolves to the user we just signed out.
 *
 * What is left afterwards is a signed-out browser, which the home already
 * knows how to render: the same dashboard, minus the account's work.
 */
export async function signOutOfSession(): Promise<void> {
  try {
    localStorage.removeItem(PHONE_SESSION_KEY);
  } catch {
    // localStorage may be unavailable in some embedded browsers.
  }
  sessionGeneration += 1;
  sessionPromise = null;
  await signOut(getFirebaseAuth());
}

function hasPhoneSessionMarker(): boolean {
  try {
    return localStorage.getItem(PHONE_SESSION_KEY) != null;
  } catch {
    return false;
  }
}

async function userHasPhone(user: User): Promise<boolean> {
  if (user.providerData.some((p) => p.providerId === 'phone')) {
    return true;
  }
  const token = await user.getIdTokenResult();
  const phone = token.claims.phone_number;
  return typeof phone === 'string' && phone.length > 0;
}

/**
 * The SDK reloads the persisted user itself while it restores the session, so
 * on a cold boot our own reload is the same `accounts:lookup` a moment later.
 * Outside that window (a long-open tab, a session that changed under us) the
 * profile is worth re-reading.
 */
const RESTORE_WINDOW_MS = 3000;

/**
 * Refresh the profile and force a fresh token, but never let either gate the
 * open: both are network round-trips, and we already hold a usable cached
 * token. If the session really is revoked the callable answers auth_required
 * and the caller falls back to phone verification — a far better outcome than
 * a spinner that never ends. The forced token is also the real revocation
 * check, which is why skipping a duplicate profile reload is safe.
 */
async function hydrateUser(user: User): Promise<User> {
  const initAt = getAuthInitAt();
  const justRestored = initAt > 0 && Date.now() - initAt < RESTORE_WINDOW_MS;
  if (!justRestored) {
    await withTimeout(user.reload(), HYDRATE_TIMEOUT_MS, 'profile reload').catch(
      () => undefined,
    );
  }
  await withTimeout(user.getIdToken(true), HYDRATE_TIMEOUT_MS, 'token refresh').catch(
    () => undefined,
  );
  return user;
}

async function waitForAuthState(auth: Auth): Promise<User | null> {
  await auth.authStateReady();
  return new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      resolve(user);
    });
  });
}

async function waitForPersistedPhoneUser(auth: Auth): Promise<User | null> {
  if (!hasPhoneSessionMarker()) return null;

  for (let attempt = 0; attempt < 40; attempt += 1) {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (user && (await userHasPhone(user))) {
      return hydrateUser(user);
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return null;
}

class SessionSupersededError extends Error {
  constructor() {
    super('session-superseded');
    this.name = 'SessionSupersededError';
  }
}

async function bootstrapSession(auth: Auth, generation: number): Promise<User> {
  const stillCurrent = () => generation === sessionGeneration;

  await waitForAuthState(auth);
  if (!stillCurrent()) throw new SessionSupersededError();

  let user = auth.currentUser;
  if (user && (await userHasPhone(user))) {
    return hydrateUser(user);
  }

  const restoredPhoneUser = await waitForPersistedPhoneUser(auth);
  if (!stillCurrent()) throw new SessionSupersededError();
  if (restoredPhoneUser) {
    return restoredPhoneUser;
  }

  user = auth.currentUser;
  if (user) {
    return hydrateUser(user);
  }

  // Creating an anonymous user after a newer sign-in is how a verified
  // executor loses their identity and home queries 403.
  if (!stillCurrent()) throw new SessionSupersededError();
  user = (await signInAnonymously(auth)).user;
  if (!stillCurrent()) throw new SessionSupersededError();
  await user.getIdToken(true);
  return user;
}

export async function ensureSessionReady(): Promise<User> {
  if (sessionPromise) return sessionPromise;

  const auth = getFirebaseAuth();
  const generation = sessionGeneration;
  // Clearing the memo on failure is what makes the caller's Retry button work;
  // a rejected promise cached here would fail every subsequent attempt.
  // Only this generation may clear the memo — a timed-out older bootstrap
  // must not wipe a session that started after a sign-out.
  sessionPromise = withTimeout(
    bootstrapSession(auth, generation),
    SESSION_TIMEOUT_MS,
    'sign-in',
  ).catch((err) => {
    if (generation === sessionGeneration) sessionPromise = null;
    throw err;
  });

  return sessionPromise;
}

/** @deprecated Use ensureSessionReady — kept for capture call sites. */
export function ensureAnonymousUser(): Promise<User> {
  return ensureSessionReady();
}

export function getCurrentUser(): User | null {
  return getFirebaseAuth().currentUser;
}

export function getCurrentUid(): string | null {
  return getFirebaseAuth().currentUser?.uid ?? null;
}

export function isPhoneVerified(): boolean {
  const user = getFirebaseAuth().currentUser;
  if (!user) return false;
  return user.providerData.some((p) => p.providerId === 'phone');
}

export async function hasVerifiedPhoneOnSession(): Promise<boolean> {
  const user = await ensureSessionReady();
  return userHasPhone(user);
}

/**
 * E.164 phone for the restored Firebase session, or null when this
 * browser is still anonymous. Used by capture PhoneStep (and execute)
 * to skip SMS re-entry on the same PWA install.
 */
export async function getVerifiedPhoneFromSession(): Promise<string | null> {
  const user = await ensureSessionReady();
  if (!(await userHasPhone(user))) return null;

  if (typeof user.phoneNumber === 'string' && user.phoneNumber.length > 0) {
    return user.phoneNumber;
  }

  const token = await user.getIdTokenResult();
  const phone = token.claims.phone_number;
  return typeof phone === 'string' && phone.length > 0 ? phone : null;
}
